import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Rpc } from './rpc';
import { RpcType } from './rpc-base';
import { RpcInboundClient } from './rpc-inbound-client';
import { RpcObservableRequest, RpcObservableRequestContext } from './rpc-observable-request';
import { RpcObservableResult, RpcObservableResultContext } from './rpc-observable-result';
import { RpcOutbound } from './rpc-outbound';
import { RpcOutboundClient } from './rpc-outbound-client';

/**
 * RPC Observable Server class.
 * Extends this class and populate rpcCall and callback properties.
 *
 * @template TRequest the request data object.
 * @template TResult the result data object.
 * @template TError the error object which must be serializable.
 */
export class RpcObservableServer<
    TRequest extends RpcObservableResultContext,
    TResult extends RpcObservableRequestContext,
    TError extends RpcObservableRequestContext> {

    private requestCommand: string;
    private resultCommand: string;
    private callback: (request: TRequest) => Observable<TResult> = () => throwError(() => new Error('not defined.'));

    /**
     * Initializes a new instance of the RpcObservableServer class.
     *
     * @param rpc the rpc object.
     * @param command the command string.
     */
    constructor(protected rpc: Rpc, protected command: string, protected version: string) {
        this.requestCommand = `${command}-Request`;
        this.resultCommand = `${command}-Result`;
        this.rpc.register(this.requestCommand, this.handler.bind(this));
        this.rpc.registerInboundHandler(this.requestCommand, this.handler.bind(this));
        this.rpc.registerOutboundHandler(this.requestCommand, this.handler.bind(this));
    }

    /**
     * Register the call back observable.
     */
    public register(value: (request: TRequest) => Observable<TResult>) {
        this.callback = value;
    }

    /**
     * Making rpc call to shell or module.
     *
     * @param result the result packet data.
     * @param outbound the outbound object from Shell to module request.
     * @return Promise<void> the promise object.
     */
    private rpcCall(result: RpcObservableResult<TResult, TError>, outbound?: RpcOutbound): Promise<void> {
        if (outbound) {
            return RpcOutboundClient.callOutbound(
                this.rpc.rpcManager.rpcChannel,
                outbound,
                this.resultCommand,
                this.version,
                result);
        }

        return RpcInboundClient.call(this.rpc, this.resultCommand, this.version, result);
    }

    /**
     * Handle the request from inbound iframe window.
     *
     * @param observableRequest the observable request data.
     */
    private handler(observableRequest: RpcObservableRequest<TRequest>): Promise<void> {
        const rpcOutbound = this.rpc.rpcManager.rpcChannel.getRpc<RpcOutbound>(
            observableRequest.sourceName,
            observableRequest.sourceSubName,
            RpcType.Outbound,
            true);
        if (!rpcOutbound) {
            const rpcInbound = this.rpc.rpcManager.rpcInbound;
            if (observableRequest.sourceName !== rpcInbound.name || observableRequest.sourceSubName !== rpcInbound.subName) {
                return Promise.reject(`Couldn't find routing back inbound/outbound windows: "${this.requestCommand}".`);
            }
        }

        observableRequest.request.sourceName = observableRequest.sourceName;
        observableRequest.request.sourceSubName = observableRequest.sourceSubName;
        observableRequest.request.sourceVersion = observableRequest.sourceVersion;
        this.callback(observableRequest.request)
            .pipe(
                catchError((error: TError) => {
                    const observableResult: RpcObservableResult<TResult, TError> = { id: observableRequest.id, error };
                    return this.rpcCall(observableResult, rpcOutbound).then(() => EMPTY, () => EMPTY);
                }),
                switchMap((result: TResult) => {
                    const observableResult: RpcObservableResult<TResult, TError> = { id: observableRequest.id, result };
                    return this.rpcCall(observableResult, rpcOutbound);
                })
            )
            .subscribe();
        return Promise.resolve();
    }
}
